import { ChevronIcon, useCloseOnOutsideAndEsc } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import { forwardRef, useMemo, useRef } from 'react';

export interface SelectProps {
	options: { value: string; label: string }[];
	value: string;
	onChange: (value: string) => void;
	name?: string;
	field?: {
		value?: string;
		onChange?: (value: string) => void;
		name?: string;
	};
}

const Selector = forwardRef<HTMLDivElement, SelectProps>(
	({ options, name, value, onChange, field, ...props }, ref) => {
		const triggerRef = useRef<HTMLButtonElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);

		const { isOpened, setIsOpened } = useCloseOnOutsideAndEsc([triggerRef, dropdownRef]);

		const val = field?.value ?? value;
		const onChng = field?.onChange ?? onChange;
		const inputName = field?.name ?? name;

		const selectedOption = useMemo(
			() => options.find((o) => String(o.value) === String(val)),
			[val, options],
		);

		const handleToggle = () => setIsOpened((prev) => !prev);
		const handleClose = () => setIsOpened(false);

		const handleSelect = (optionValue: string) => {
			onChng(optionValue);
			handleClose();
		};

		return (
			<div ref={ref} className={clsx('relative inline-block')} {...props}>
				<input name={inputName} type="hidden" value={val ?? ''} />

				<button
					ref={triggerRef}
					className={clsx(
						'relative flex gap-space-4 items-center justify-center cursor-pointer mr-space-12',
						'text-neutral-950 hover:text-neutral-700 transition-colors uppercase text-base-medium',
					)}
					onClick={handleToggle}
					type="button"
				>
					<span className="mr-space-4">
						{selectedOption?.label ?? selectedOption?.value}
					</span>

					<ChevronIcon
						className={clsx(
							'w-4 h-4 transition-transform duration-200',
							isOpened ? 'rotate-180' : 'rotate-0',
						)}
					/>
				</button>

				{isOpened && (
					<div
						ref={dropdownRef}
						className={clsx(
							'absolute z-50 mt-space-4 bg-white border border-neutral-100 rounded-md shadow-md',
						)}
					>
						<div className="p-space-4 max-h-60 overflow-y-auto">
							{options.map((option) => {
								const isSelected = option.value === val;

								return (
									<button
										key={option.value}
										className={clsx(
											'w-full text-left cursor-pointer select-none px-space-12 py-space-8 rounded-md text-sm text-neutral-950',
											'hover:bg-neutral-50 focus:bg-neutral-50',
											isSelected && 'bg-brand-secondary-50 font-medium',
										)}
										onClick={() => handleSelect(option.value)}
										type="button"
									>
										{option.label}
									</button>
								);
							})}
						</div>
					</div>
				)}
			</div>
		);
	},
);

Selector.displayName = 'Selector';

export default Selector;
